/**
 * Invoice history section with pagination
 */

import { Download01, Receipt } from '@untitledui/icons';
import { useEffect } from 'react';

import { useInvoices } from '@/hooks/billing/useBilling';
import { BadgeWithDot } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { PaginationCardMinimal } from '@/lib/base/pagination';
import { Table } from '@/lib/base/table';
import type { Invoice, InvoiceStatus } from '@/lib/billing/types';

interface InvoiceHistorySectionProps {
  orgId: number;
}

const STATUS_BADGE_COLOR: Record<
  InvoiceStatus,
  'success' | 'warning' | 'error' | 'gray'
> = {
  paid: 'success',
  open: 'warning',
  draft: 'gray',
  uncollectible: 'error',
  void: 'gray',
};

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100); // Stripe amounts are in cents
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  const color = STATUS_BADGE_COLOR[status] || 'gray';
  const label = status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <BadgeWithDot type='pill-color' size='sm' color={color}>
      {label}
    </BadgeWithDot>
  );
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  const handleDownload = () => {
    if (invoice.pdf_url) {
      window.open(invoice.pdf_url, '_blank');
    }
  };

  return (
    <Table.Row>
      <Table.Cell>
        <span className='text-sm font-medium text-primary'>
          {invoice.number || invoice.id}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span className='text-sm text-tertiary'>
          {formatDate(invoice.created)}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span className='text-sm font-medium text-primary'>
          {formatCurrency(invoice.amount, invoice.currency)}
        </span>
      </Table.Cell>
      <Table.Cell>
        <InvoiceStatusBadge status={invoice.status} />
      </Table.Cell>
      <Table.Cell>
        {invoice.pdf_url && (
          <Button
            color='link-gray'
            size='sm'
            iconLeading={Download01}
            onClick={handleDownload}
            aria-label='Download invoice'
          >
            Download
          </Button>
        )}
      </Table.Cell>
    </Table.Row>
  );
}

export function InvoiceHistorySection({ orgId }: InvoiceHistorySectionProps) {
  const {
    invoices,
    loading,
    error,
    totalPages,
    currentPage,
    loadInvoices,
    goToPage,
  } = useInvoices(orgId);

  // Load invoices on mount
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  return (
    <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm overflow-hidden'>
      <div className='px-6 py-5 border-b border-border-secondary'>
        <div className='flex items-center gap-3'>
          <FeaturedIcon icon={Receipt} size='sm' theme='modern' color='gray' />
          <div>
            <h2 className='text-lg font-semibold text-secondary'>
              Invoice history
            </h2>
            <p className='text-sm text-tertiary mt-0.5'>
              View and download past invoices
            </p>
          </div>
        </div>
      </div>

      <div className='p-6'>
        {(() => {
          let invoicesContent: React.ReactNode;
          if (loading) {
            invoicesContent = (
              <div className='flex justify-center py-8'>
                <LoadingIndicator size='md' />
              </div>
            );
          } else if (error) {
            invoicesContent = (
              <div className='text-center py-8'>
                <p className='text-sm text-error-primary mb-4'>{error}</p>
                <Button
                  color='secondary'
                  size='sm'
                  onClick={() => loadInvoices()}
                >
                  Retry
                </Button>
              </div>
            );
          } else if (invoices.length === 0) {
            invoicesContent = (
              <div className='text-center py-8'>
                <p className='text-sm text-tertiary'>No invoices yet</p>
              </div>
            );
          } else {
            invoicesContent = (
              <>
                <Table size='sm'>
                  <Table.Header>
                    <Table.Row>
                      <Table.Head label='Invoice' isRowHeader />
                      <Table.Head label='Date' />
                      <Table.Head label='Amount' />
                      <Table.Head label='Status' />
                      <Table.Head label='Actions' />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {invoices.map(invoice => (
                      <InvoiceRow key={invoice.id} invoice={invoice} />
                    ))}
                  </Table.Body>
                </Table>

                {totalPages > 1 && (
                  <PaginationCardMinimal
                    page={currentPage}
                    total={totalPages}
                    align='center'
                    onPageChange={goToPage}
                    className='mt-4 border-t-0'
                  />
                )}
              </>
            );
          }
          return invoicesContent;
        })()}
      </div>
    </div>
  );
}
